import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table,
} from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

/**
 * Org Admin — define quality grades per category (materials & products).
 * Criteria (e.g. Brix min/max) drive QC auto-grading.
 */
function OrganizationQualityGrades({ currencySymbol = '' }) {
  const [loading, setLoading] = useState(true);
  const [itemTypes, setItemTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [grades, setGrades] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '',
    label: '',
    description: '',
    scope: 'BOTH',
    sortOrder: 0,
    isActive: true,
    referenceRate: '',
    referenceRateUomId: '',
    criteria: [{ code: 'brix', name: 'Brix', unit: '%', min: '', max: '' }],
  });

  const fetchItemTypes = useCallback(async () => {
    try {
      const res = await axios.get('/api/organization-quality-grades/item-types');
      const list = Array.isArray(res.data) ? res.data : [];
      setItemTypes(list);
      setSelectedTypeId((prev) => {
        if (prev) return prev;
        return list.length ? String(list[0].id) : '';
      });
      // The grades load only follows a chosen type; with none to choose there is nothing more to wait for
      if (!list.length) setLoading(false);
    } catch (e) {
      toast.error(e.response?.data || 'Failed to load categories for grading');
      setItemTypes([]);
      setLoading(false);
    }
  }, []);

  const fetchUoms = useCallback(async () => {
    try {
      const res = await axios.get('/api/inventory/units-of-measurement');
      const list = Array.isArray(res.data) ? res.data : [];
      setUoms(list.filter((u) => u.isActive !== false));
    } catch {
      setUoms([]);
    }
  }, []);

  const fetchItems = useCallback(async (typeId) => {
    if (!typeId) {
      setItems([]);
      return;
    }
    try {
      const res = await axios.get(`/api/organization-quality-grades/item-types/${typeId}/items`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data || 'Failed to load items for this category');
      setItems([]);
    }
  }, []);

  const fetchGrades = useCallback(async (typeId, itemId) => {
    if (!typeId) {
      setGrades([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get('/api/organization-quality-grades', {
        params: itemId ? { itemId } : { itemTypeId: typeId },
      });
      setGrades(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(typeof e.response?.data === 'string' ? e.response.data : 'Failed to load grades');
      setGrades([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItemTypes();
    fetchUoms();
  }, [fetchItemTypes, fetchUoms]);

  useEffect(() => {
    setSelectedItemId('');
    if (selectedTypeId) fetchItems(selectedTypeId);
  }, [selectedTypeId, fetchItems]);

  useEffect(() => {
    if (selectedTypeId) fetchGrades(selectedTypeId, selectedItemId);
  }, [selectedTypeId, selectedItemId, fetchGrades]);

  const selectedType = itemTypes.find((t) => String(t.id) === String(selectedTypeId));
  const selectedItem = items.find((i) => String(i.id) === String(selectedItemId));

  const uomLabel = (uom) => {
    if (!uom) return '';
    if (uom.symbol && uom.name) return `${uom.symbol} (${uom.name})`;
    return uom.symbol || uom.name || String(uom.id);
  };

  const formatReferenceRate = (grade) => {
    if (grade.referenceRate == null) return null;
    const uom = grade.referenceRateUom;
    const per = uom ? (uom.symbol || uom.name) : null;
    return per
      ? `${currencySymbol}${grade.referenceRate} / ${per}`
      : `${currencySymbol}${grade.referenceRate}`;
  };

  const parseCriteria = (grade) => {
    if (!grade?.criteriaJson) return [];
    try {
      const parsed = JSON.parse(grade.criteriaJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const openCreate = () => {
    setEditing(null);
    const scopeSource = selectedItem || selectedType;
    const defaultScope = scopeSource?.source === 'PRODUCT' ? 'PRODUCT'
      : scopeSource?.source === 'MATERIAL' ? 'MATERIAL' : 'BOTH';
    setForm({
      code: '',
      label: '',
      description: '',
      scope: defaultScope,
      sortOrder: grades.length,
      isActive: true,
      referenceRate: '',
      referenceRateUomId: '',
      criteria: [{ code: 'brix', name: 'Brix', unit: '%', min: '', max: '' }],
    });
    setShowModal(true);
  };

  const openEdit = (grade) => {
    setEditing(grade);
    const criteria = parseCriteria(grade).map((c) => ({
      code: c.code || '',
      name: c.name || '',
      unit: c.unit || '',
      min: c.min != null ? String(c.min) : '',
      max: c.max != null ? String(c.max) : '',
    }));
    setForm({
      code: grade.code || '',
      label: grade.label || '',
      description: grade.description || '',
      scope: grade.scope || 'BOTH',
      sortOrder: grade.sortOrder ?? 0,
      isActive: grade.isActive !== false,
      referenceRate: grade.referenceRate != null ? String(grade.referenceRate) : '',
      referenceRateUomId: grade.referenceRateUom?.id != null ? String(grade.referenceRateUom.id) : '',
      criteria: criteria.length ? criteria : [{ code: '', name: '', unit: '', min: '', max: '' }],
    });
    setShowModal(true);
  };

  const updateCriterion = (idx, field, value) => {
    setForm((prev) => {
      const criteria = [...prev.criteria];
      criteria[idx] = { ...criteria[idx], [field]: value };
      return { ...prev, criteria };
    });
  };

  const addCriterion = () => {
    setForm((prev) => ({
      ...prev,
      criteria: [...prev.criteria, { code: '', name: '', unit: '', min: '', max: '' }],
    }));
  };

  const removeCriterion = (idx) => {
    setForm((prev) => ({
      ...prev,
      criteria: prev.criteria.filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedTypeId) return;
    try {
      setSaving(true);
      const criteria = form.criteria
        .filter((c) => (c.code || '').trim())
        .map((c) => ({
          code: c.code.trim().toLowerCase(),
          name: (c.name || c.code).trim(),
          unit: (c.unit || '').trim() || null,
          min: c.min !== '' ? Number(c.min) : null,
          max: c.max !== '' ? Number(c.max) : null,
        }));
      const hasRate = form.referenceRate !== '';
      if (hasRate && !form.referenceRateUomId) {
        toast.error('Reference rate requires a unit (e.g. per ton)');
        setSaving(false);
        return;
      }
      const payload = {
        code: form.code,
        label: form.label,
        description: form.description || null,
        scope: form.scope,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.isActive,
        referenceRate: hasRate ? form.referenceRate : null,
        referenceRateUomId: hasRate ? form.referenceRateUomId : null,
        criteria,
      };
      if (editing) {
        await axios.put(`/api/organization-quality-grades/${editing.id}`, payload);
        toast.success('Quality grade updated');
      } else {
        await axios.post(`/api/organization-quality-grades/item-types/${selectedTypeId}`, {
          ...payload,
          itemId: selectedItemId || null,
        });
        toast.success('Quality grade created');
      }
      setShowModal(false);
      fetchGrades(selectedTypeId, selectedItemId);
    } catch (err) {
      const msg = err.response?.data;
      toast.error(typeof msg === 'string' ? msg : 'Failed to save quality grade');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (grade) => {
    if (!window.confirm(`Delete grade ${grade.code}?`)) return;
    try {
      await axios.delete(`/api/organization-quality-grades/${grade.id}`);
      toast.success('Grade deleted');
      fetchGrades(selectedTypeId, selectedItemId);
    } catch (err) {
      const msg = err.response?.data;
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete grade');
    }
  };

  const formatCriteria = (grade) => {
    const rules = parseCriteria(grade);
    if (!rules.length) return <span className="text-muted">Manual only</span>;
    return rules.map((r) => {
      const parts = [];
      if (r.min != null) parts.push(`≥ ${r.min}`);
      if (r.max != null) parts.push(`≤ ${r.max}`);
      const range = parts.length ? parts.join(' & ') : 'any';
      return `${r.name || r.code}: ${range}${r.unit ? ` ${r.unit}` : ''}`;
    }).join(' · ');
  };

  const sourceBadge = (type) => {
    if (!type) return null;
    if (type.source === 'BOTH') return <Badge bg="primary">Material + Product</Badge>;
    if (type.source === 'MATERIAL') return <Badge bg="secondary">Material</Badge>;
    if (type.source === 'PRODUCT') return <Badge bg="info">Product</Badge>;
    return null;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <p className="text-muted mb-0">
            Define grades for a category, or for one specific item when the type's members
            don't share a grading scale (e.g. Sodium Hydrosulfite within a broader chemicals type).
            Set measurement rules (e.g. Brix ≥ 18 → Grade A) so QC can auto-calculate the grade.
          </p>
        </div>
        <Button variant="primary" onClick={openCreate} disabled={!selectedTypeId}>
          + Add Grade
        </Button>
      </div>

      {itemTypes.length === 0 ? (
        <Alert variant="info">
          Enable materials under <strong>Materials</strong> and/or create <strong>Products</strong> first.
          Grades are configured per category from those catalogs.
        </Alert>
      ) : (
        <Card className="mb-3">
          <Card.Body>
            <Row className="align-items-end">
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={selectedTypeId}
                    onChange={(e) => setSelectedTypeId(e.target.value)}
                  >
                    {itemTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Applies to</Form.Label>
                  <Form.Select
                    value={selectedItemId}
                    onChange={(e) => setSelectedItemId(e.target.value)}
                  >
                    <option value="">All items of this type</option>
                    {items.map((i) => (
                      <option key={i.id} value={i.id}>{i.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Text>
                    Pick one item if it needs its own grade scale instead of the whole category's.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={2} className="pb-2">
                {sourceBadge(selectedItem || selectedType)}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" />
        </div>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Label</th>
                  <th>Scope</th>
                  <th>Auto-grade rules</th>
                  <th>Ref. rate</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {grades.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted">
                      {selectedItemId
                        ? `No grades yet scoped to ${selectedItem?.name || 'this item'}. Add Grade A / B with Brix (or other) rules.`
                        : 'No org grades yet for this category. Add Grade A / B with Brix (or other) rules.'}
                      {' '}Until then, QC falls back to {selectedItemId ? 'the category\'s grades, then ' : ''}System Admin templates if any.
                    </td>
                  </tr>
                ) : (
                  grades.map((g) => (
                    <tr key={g.id}>
                      <td><code>{g.code}</code></td>
                      <td>{g.label}</td>
                      <td>{g.scope || 'BOTH'}</td>
                      <td style={{ maxWidth: 320, fontSize: '0.9em' }}>{formatCriteria(g)}</td>
                      <td>
                        {g.referenceRate != null
                          ? formatReferenceRate(g)
                          : <span className="text-muted">—</span>}
                      </td>
                      <td>{g.sortOrder}</td>
                      <td>
                        {g.isActive !== false
                          ? <Badge bg="success">Active</Badge>
                          : <Badge bg="secondary">Inactive</Badge>}
                      </td>
                      <td>
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(g)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(g)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title>{editing ? 'Edit Quality Grade' : 'Add Quality Grade'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!editing && (
              <Alert variant="light" className="border py-2 mb-3">
                Applies to: <strong>{selectedItem ? selectedItem.name : (selectedType?.name || '')}</strong>
                {!selectedItem && <span className="text-muted"> (every item of this type)</span>}
              </Alert>
            )}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Code *</Form.Label>
                  <Form.Control
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="A"
                    required
                    disabled={!!editing}
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Label *</Form.Label>
                  <Form.Control
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="Grade A"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Applies to</Form.Label>
                  <Form.Select
                    value={form.scope}
                    onChange={(e) => setForm({ ...form, scope: e.target.value })}
                  >
                    <option value="BOTH">Materials &amp; Products</option>
                    <option value="MATERIAL">Materials only (incoming)</option>
                    <option value="PRODUCT">Products only (production FG)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sort order</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                  />
                  <Form.Text>Lower order is evaluated first for auto-grade (put Grade A first).</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Reference rate {currencySymbol ? `(${currencySymbol.trim()})` : ''}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.referenceRate}
                    onChange={(e) => setForm({
                      ...form,
                      referenceRate: e.target.value,
                      referenceRateUomId: e.target.value === '' ? '' : form.referenceRateUomId,
                    })}
                    placeholder="e.g. 3500"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Per unit {form.referenceRate !== '' ? '*' : ''}</Form.Label>
                  <Form.Select
                    value={form.referenceRateUomId}
                    onChange={(e) => setForm({ ...form, referenceRateUomId: e.target.value })}
                    required={form.referenceRate !== ''}
                    disabled={form.referenceRate === ''}
                  >
                    <option value="">Select UOM</option>
                    {uoms.map((u) => (
                      <option key={u.id} value={u.id}>{uomLabel(u)}</option>
                    ))}
                  </Form.Select>
                  <Form.Text>
                    Required with a rate — e.g. {currencySymbol || ''}3500 / ton.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Form.Group>
            <Form.Check
              type="switch"
              className="mb-3"
              label="Active"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />

            <hr />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Auto-grade measurements</strong>
              <Button type="button" size="sm" variant="outline-secondary" onClick={addCriterion}>
                + Measurement
              </Button>
            </div>
            <p className="text-muted small">
              Example: Brix min 18 → Grade A; Brix min 14 max 17 → Grade B.
              QC enters values; ERP picks the first matching grade (by sort order).
            </p>
            {form.criteria.map((c, idx) => (
              <Row key={idx} className="g-2 mb-2 align-items-end">
                <Col md={2}>
                  <Form.Label className="small">Code</Form.Label>
                  <Form.Control
                    value={c.code}
                    onChange={(e) => updateCriterion(idx, 'code', e.target.value)}
                    placeholder="brix"
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Name</Form.Label>
                  <Form.Control
                    value={c.name}
                    onChange={(e) => updateCriterion(idx, 'name', e.target.value)}
                    placeholder="Brix"
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="small">Unit</Form.Label>
                  <Form.Control
                    value={c.unit}
                    onChange={(e) => updateCriterion(idx, 'unit', e.target.value)}
                    placeholder="%"
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="small">Min</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={c.min}
                    onChange={(e) => updateCriterion(idx, 'min', e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="small">Max</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={c.max}
                    onChange={(e) => updateCriterion(idx, 'max', e.target.value)}
                  />
                </Col>
                <Col md={1}>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeCriterion(idx)}
                    disabled={form.criteria.length <= 1}
                  >
                    ×
                  </Button>
                </Col>
              </Row>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default OrganizationQualityGrades;
